import type { Instance } from "mobx-state-tree";
import { types, flow } from "mobx-state-tree";
import type { RealtimeChannel } from "@supabase/supabase-js";
import type { ProjectMessage } from "@/domains/messages/types";
import { applyReactionToggle } from "@/domains/messages/reactions";
import { registerFocusRevalidation } from "@/lib/realtime/revalidateOnFocus";
import * as messagesRepo from "./repository";

/**
 * Per-project message board store. Owns the active channel's feed plus its two
 * realtime channels (messages + reactions). Volatile because the feed is large,
 * frequently replaced wholesale, and never persisted in a snapshot.
 *
 * Conventions mirror src/stores/pm/IssuesStore: write-then-refetch for sends/
 * edits/deletes, unsubscribe-before-subscribe lifecycle, components observe.
 */
export const MessagesStore = types
  .model("MessagesStore", {})
  .volatile(() => ({
    messages: [] as ProjectMessage[],
    isLoading: false as boolean,
    error: null as string | null,
    /** Channel currently loaded into `messages` — guards stale realtime reloads. */
    currentProjectId: null as number | null,
    channel: null as RealtimeChannel | null,
    reactionChannel: null as RealtimeChannel | null,
    /** Unread @-mentions for the badge on the dashboard's MESSAGE BOARD label row. */
    unreadMentionCount: 0 as number,
    /** Scope the unread badge counts over — set by the dashboard widget. */
    unreadContext: null as { buildingId: number; userId: number; projectId: number | null } | null,
    /** Building-wide realtime channel that recounts the badge on any message/
     *  mention activity — without it a mention in a non-selected channel would
     *  stay invisible until the user switched into that channel. */
    mentionBadgeChannel: null as RealtimeChannel | null,
    unreadFocusTeardown: null as (() => void) | null,
  }))
  .actions((self) => ({
    refreshUnreadMentions: flow(function* refreshUnreadMentions() {
      const ctx = self.unreadContext;
      if (!ctx) return;
      try {
        const count: number = yield messagesRepo.getUnreadMentionCount(
          ctx.buildingId,
          ctx.userId,
          ctx.projectId
        );
        // Drop a stale response if the context changed while in flight.
        if (self.unreadContext === ctx) self.unreadMentionCount = count;
      } catch (e) {
        console.error("[MessagesStore.refreshUnreadMentions] failed:", e);
      }
    }),
  }))
  .actions((self) => ({
    loadMessages: flow(function* loadMessages(buildingProjectId: number, currentUserId: number) {
      self.isLoading = true;
      self.error = null;
      self.currentProjectId = buildingProjectId;
      try {
        const messages: ProjectMessage[] = yield messagesRepo.listMessages(
          buildingProjectId,
          currentUserId
        );
        // Ignore a response for a channel the user has since switched away from.
        if (self.currentProjectId !== buildingProjectId) return;
        self.messages = messages;
        // Keep the unread badge fresh — realtime reloads route through here,
        // so a new incoming @-mention bumps the badge without extra wiring.
        void self.refreshUnreadMentions();
      } catch (e) {
        self.error = e instanceof Error ? e.message : "Failed to load messages";
      } finally {
        if (self.currentProjectId === buildingProjectId) self.isLoading = false;
      }
    }),
  }))
  .actions((self) => ({
    sendMessage: flow(function* sendMessage(
      buildingProjectId: number,
      authorId: number,
      body: string,
      mentionedUserIds: number[] = [],
      documentIds: number[] = [],
      replyToId: number | null = null
    ) {
      const message: ProjectMessage = yield messagesRepo.createMessage(
        {
          building_project_id: buildingProjectId,
          author_id: authorId,
          body,
          reply_to_id: replyToId,
        },
        mentionedUserIds,
        documentIds
      );
      // Fire the email notification best-effort (mirrors the org-invite flow).
      // Not awaited: a slow/failed email must never delay or break posting.
      void messagesRepo.notifyProjectMessage(message.id);
      yield self.loadMessages(buildingProjectId, authorId);
    }),

    editMessage: flow(function* editMessage(
      messageId: number,
      buildingProjectId: number,
      body: string,
      currentUserId: number,
      mentionedUserIds: number[] = []
    ) {
      yield messagesRepo.updateMessage(messageId, body, mentionedUserIds);
      yield self.loadMessages(buildingProjectId, currentUserId);
    }),

    deleteMessage: flow(function* deleteMessage(
      messageId: number,
      buildingProjectId: number,
      currentUserId: number
    ) {
      yield messagesRepo.softDeleteMessage(messageId);
      yield self.loadMessages(buildingProjectId, currentUserId);
    }),

    toggleReaction: flow(function* toggleReaction(
      messageId: number,
      userId: number,
      emoji: string
    ) {
      // Optimistic: reassign the message with the toggled summary so observers
      // re-render instantly. The reaction realtime channel reloads the
      // canonical counts shortly after.
      self.messages = self.messages.map((m) =>
        m.id === messageId ? { ...m, reactions: applyReactionToggle(m.reactions, emoji) } : m
      );
      try {
        yield messagesRepo.toggleReaction(messageId, userId, emoji);
      } catch (e) {
        // Roll back by reloading the canonical feed.
        if (self.currentProjectId != null) {
          yield self.loadMessages(self.currentProjectId, userId);
        }
        throw e;
      }
    }),

    /** Best-effort: mark this channel's @-mentions read for the user. A failure
     *  here must not break the board, so errors are swallowed (logged). */
    markChannelMentionsReadSafe: flow(function* markChannelMentionsReadSafe(
      buildingProjectId: number,
      currentUserId: number
    ) {
      try {
        yield messagesRepo.markChannelMentionsRead(buildingProjectId, currentUserId);
      } catch (e) {
        console.error("[MessagesStore.markChannelMentionsRead] failed:", e);
      }
      // Reading a channel consumes its mentions — recount so the badge drops.
      void self.refreshUnreadMentions();
    }),

    /** Scope the unread badge (whole building, or one project), refresh it, and
     *  keep it live via a building-wide realtime subscription. */
    setUnreadContext(buildingId: number, userId: number, projectId: number | null) {
      self.unreadContext = { buildingId, userId, projectId };
      self.mentionBadgeChannel?.unsubscribe();
      self.mentionBadgeChannel = messagesRepo.subscribeToMentionBadge(buildingId, userId, () => {
        void self.refreshUnreadMentions();
      });
      // The channel's `project_message_mentions` half is dead until that table
      // is published (see subscribeToMentionBadge), so a mark-read on another
      // tab or device never reaches this one. Coming back to the tab is the
      // only signal we have for it.
      self.unreadFocusTeardown?.();
      self.unreadFocusTeardown = registerFocusRevalidation(() => {
        void self.refreshUnreadMentions();
      });
      void self.refreshUnreadMentions();
    },

    /** Tear down the badge scope + its realtime channel (widget unmount). */
    clearUnreadContext() {
      self.mentionBadgeChannel?.unsubscribe();
      self.mentionBadgeChannel = null;
      self.unreadFocusTeardown?.();
      self.unreadFocusTeardown = null;
      self.unreadContext = null;
      self.unreadMentionCount = 0;
    },

    subscribe(buildingProjectId: number, reloadFn: () => void) {
      self.channel?.unsubscribe();
      self.reactionChannel?.unsubscribe();
      self.channel = messagesRepo.subscribeToMessages(buildingProjectId, reloadFn);
      self.reactionChannel = messagesRepo.subscribeToReactions(buildingProjectId, reloadFn);
    },

    unsubscribe() {
      self.channel?.unsubscribe();
      self.reactionChannel?.unsubscribe();
      self.channel = null;
      self.reactionChannel = null;
    },

    /** Clear the feed when switching away so the next channel doesn't flash
     *  the previous channel's messages before its load resolves. */
    reset() {
      self.messages = [];
      self.error = null;
      self.currentProjectId = null;
    },
  }));

export type MessagesStoreInstance = Instance<typeof MessagesStore>;
